// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import {
  allUserPermissionsQueryOptions,
  userRoleDetailQueryOptions,
  userRoleListQueryOptions,
  userRolesContextQueryOptions,
  userRoleAutoGrantsQueryOptions,
} from '@/modules/user-role/user-role.hooks'

export const userRolesRoute = createRoute({
  path: '/user-roles',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const userRolesListRoute = createRoute({
  getParentRoute: () => userRolesRoute,
  path: '/',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'user-roles.list',
      'Oyuncu rol listesine erişim izniniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Oyuncu Rol Listesi' }],
  }),
  loader: async () => {
    await queryClient.ensureQueryData(userRoleListQueryOptions())
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/user-role/pages/list-user-role.page')
  ),
})

export const userRolesCreateRoute = createRoute({
  getParentRoute: () => userRolesRoute,
  path: 'create',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'user-roles.create',
      'Oyuncu rolü oluşturmak için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Oyuncu Rolü Oluştur' }],
  }),
  loader: async () => {
    await queryClient.ensureQueryData(allUserPermissionsQueryOptions())
  },
  component: lazyRouteComponent(
    () => import('@/modules/user-role/pages/create-user-role.page')
  ),
})

export const userRolesDetailRoute = createRoute({
  getParentRoute: () => userRolesRoute,
  path: '/$id',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'user-roles.detail',
      'Oyuncu rol detayına erişim izniniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Oyuncu Rolü Detayı' }],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(userRoleDetailQueryOptions(id)),
      queryClient.ensureQueryData(allUserPermissionsQueryOptions()),
      queryClient.ensureQueryData(userRolesContextQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/user-role/pages/user-role-detail.page')
  ),
})

export const userRolesEditRoute = createRoute({
  getParentRoute: () => userRolesRoute,
  path: '/$id/edit',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'user-roles.edit',
      'Oyuncu rol düzenlemek için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Oyuncu Rolü Düzenle' }],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(userRoleDetailQueryOptions(id)),
      queryClient.ensureQueryData(allUserPermissionsQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/user-role/pages/edit-user-role.page')
  ),
})

export const userRolesAutoGrantsRoute = createRoute({
  getParentRoute: () => userRolesRoute,
  path: '/$id/auto-grants',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'user-roles.edit',
      'Otomatik rol atama kurallarını düzenlemek için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Otomatik Rol Atama Kuralları' }],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(userRoleDetailQueryOptions(id)),
      queryClient.ensureQueryData(userRoleAutoGrantsQueryOptions(id)),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/user-role/pages/auto-grants-user-role.page')
  ),
})
